import { Loader2, MapPin, XCircle } from "lucide-react";
import { LastSeen } from "./LastSeen";

export function UserDetailsModal({ details, onClose }) {
  if (!details) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="user-details-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#EAEAEA] px-5 py-4 flex items-center justify-between z-10">
          <h2 className="font-bold text-lg text-[#1A1A1A]">تفاصيل المستخدم</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[#F5F5F5] flex items-center justify-center"
            data-testid="close-user-details-btn"
          >
            <XCircle className="w-5 h-5 text-[#6B6B6B]" />
          </button>
        </div>

        {details.loading ? (
          <div className="p-10 text-center text-sm text-[#6B6B6B]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            جاري تحميل البيانات...
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Header: name + avatar + role */}
            <div className="flex items-center gap-3 pb-4 border-b border-[#EAEAEA]">
              {details.user?.picture ? (
                <img src={details.user.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#E05D3A] text-white flex items-center justify-center font-bold text-lg">
                  {details.user?.name?.[0]?.toUpperCase() || "U"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-[#1A1A1A] text-base">{details.user?.name || "(بدون اسم)"}</h3>
                <p className="text-xs text-[#6B6B6B] break-all">{details.user?.user_id}</p>
              </div>
              <span className={`text-[11px] px-2.5 py-1 rounded-full font-bold ${
                details.role === "admin" ? "bg-purple-100 text-purple-700" :
                details.role === "driver" ? "bg-blue-100 text-blue-700" :
                details.role === "owner" ? "bg-amber-100 text-amber-700" :
                "bg-green-100 text-green-700"
              }`}>
                {details.role === "admin" ? "أدمن" : details.role === "driver" ? "سائق" : details.role === "owner" ? "مطعم" : "عميل"}
              </span>
            </div>

            {/* Personal info */}
            <div>
              <h4 className="font-bold text-sm text-[#E05D3A] mb-2">المعلومات الشخصية</h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <InfoRow label="البريد الإلكتروني" value={details.user?.email || "—"} />
                <InfoRow label="رقم الجوال" value={details.user?.phone || "—"} />
                <InfoRow label="طريقة التسجيل" value={details.user?.auth_provider === "google" ? "Google" : details.user?.auth_provider === "phone" ? "جوال (OTP)" : "إيميل/كلمة مرور"} />
                <InfoRow label="تاريخ الانضمام" value={details.user?.created_at ? new Date(details.user.created_at).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" }) : "—"} />
                <InfoRow label="آخر ظهور" value={<LastSeen iso={details.user?.last_seen_at} withTime />} />
                {details.user?.updated_at && (
                  <InfoRow label="آخر تحديث" value={new Date(details.user.updated_at).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" })} />
                )}
              </dl>
            </div>

            {/* Signup location */}
            <div>
              <h4 className="font-bold text-sm text-[#E05D3A] mb-2 flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> موقع التسجيل (Signup Location)
              </h4>
              <LocationBlock data={details.signup_location} emptyText="لم تُسجَّل بيانات الموقع لهذا المستخدم (تسجيل قبل تفعيل الميزة)." />
            </div>

            {/* Last login location */}
            <div>
              <h4 className="font-bold text-sm text-[#E05D3A] mb-2 flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> آخر دخول (Last Login)
              </h4>
              <LocationBlock data={details.last_login} emptyText="لم يسجّل الدخول بعد تفعيل تتبّع الموقع." />
            </div>

            {/* Stats grid */}
            <div>
              <h4 className="font-bold text-sm text-[#E05D3A] mb-2">إحصائيات</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatCard label="إجمالي الطلبات" value={details.stats?.orders_total || 0} />
                <StatCard label="مبلغ الإنفاق" value={`${details.stats?.total_spent || 0} ر.س`} />
                <StatCard label="رصيد المحفظة" value={`${details.stats?.wallet_balance || 0} ر.س`} />
                <StatCard label="المفضّلة" value={details.stats?.favorites_count || 0} />
                <StatCard label="أجهزة Push" value={details.stats?.devices_count || 0} />
                <StatCard label="جلسات نشطة" value={details.stats?.active_sessions || 0} />
                <StatCard label="إشعارات (كل)" value={details.stats?.notifications_total || 0} />
                <StatCard label="إشعارات غير مقروءة" value={details.stats?.notifications_unread || 0} highlight={(details.stats?.notifications_unread || 0) > 0} />
              </div>
            </div>

            {/* Orders by status */}
            {details.stats?.orders_by_status && Object.keys(details.stats.orders_by_status).length > 0 && (
              <div>
                <h4 className="font-bold text-sm text-[#E05D3A] mb-2">الطلبات حسب الحالة</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(details.stats.orders_by_status).map(([status, count]) => (
                    <span key={status} className="text-xs px-3 py-1 rounded-full bg-[#F5F5F5] text-[#1A1A1A]">
                      {status}: <b>{count}</b>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recent orders */}
            {details.recent_orders?.length > 0 && (
              <div>
                <h4 className="font-bold text-sm text-[#E05D3A] mb-2">آخر 10 طلبات</h4>
                <div className="rounded-xl border border-[#EAEAEA] divide-y divide-[#EAEAEA] max-h-[240px] overflow-y-auto">
                  {details.recent_orders.map((o, i) => (
                    <div key={o.order_id || i} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-[#1A1A1A] truncate">{o.restaurant || "—"}</p>
                        <p className="text-[10px] text-[#9B9B9B]">
                          {o.order_id} · {o.items_count} أصناف · {o.created_at ? new Date(o.created_at).toLocaleString("ar-SA", { dateStyle: "short", timeStyle: "short" }) : ""}
                        </p>
                      </div>
                      <div className="text-end whitespace-nowrap">
                        <p className="font-bold text-[#1A1A1A]">{o.total} ر.س</p>
                        <p className="text-[10px] text-[#6B6B6B]">{o.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Addresses */}
            {details.addresses?.length > 0 && (
              <div>
                <h4 className="font-bold text-sm text-[#E05D3A] mb-2">العناوين المحفوظة ({details.addresses.length})</h4>
                <div className="space-y-2">
                  {details.addresses.map((a, i) => (
                    <div key={a.id || i} className="rounded-xl border border-[#EAEAEA] px-3 py-2 text-xs">
                      <p className="font-bold text-[#1A1A1A]">{a.label || a.name || `عنوان ${i + 1}`}</p>
                      <p className="text-[#6B6B6B] mt-0.5">{a.address || a.street || "—"}</p>
                      {(a.lat && a.lng) && (
                        <p className="text-[10px] text-[#9B9B9B] mt-0.5">📍 {a.lat}, {a.lng}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Devices */}
            {details.devices?.length > 0 && (
              <div>
                <h4 className="font-bold text-sm text-[#E05D3A] mb-2">الأجهزة المسجّلة للإشعارات ({details.devices.length})</h4>
                <div className="space-y-1.5">
                  {details.devices.map((d, i) => (
                    <div key={i} className="rounded-lg bg-[#F5F5F5] px-3 py-1.5 text-xs flex items-center justify-between">
                      <span className="font-bold text-[#1A1A1A]">{d.platform}</span>
                      <span className="text-[10px] text-[#6B6B6B]">...{d.token_suffix}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-[10px] text-[#9B9B9B] text-center pt-2 border-t border-[#EAEAEA]">
              🔒 كلمة المرور مشفّرة بـ bcrypt ولا يمكن عرضها لأي شخص (سياسة أمان صارمة).
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2 py-1">
      <dt className="text-[#6B6B6B]">{label}</dt>
      <dd className="text-[#1A1A1A] font-medium text-end break-all">{value}</dd>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-xl px-3 py-2.5 border ${highlight ? "border-[#FFD8C9] bg-[#FFF4F0]" : "border-[#EAEAEA] bg-[#FAFAFA]"}`}>
      <p className="text-[10px] text-[#6B6B6B] mb-0.5">{label}</p>
      <p className={`font-bold text-sm ${highlight ? "text-[#E05D3A]" : "text-[#1A1A1A]"}`}>{value}</p>
    </div>
  );
}

function LocationBlock({ data, emptyText }) {
  const geo = data?.geo || {};
  const hasGeo = !!(geo.country || geo.city || geo.lat);
  const hasIp = !!data?.ip;
  if (!hasIp && !hasGeo) {
    return (
      <div className="rounded-xl border border-dashed border-[#EAEAEA] bg-[#FAFAFA] p-3 text-xs text-[#9B9B9B] text-center">
        {emptyText}
      </div>
    );
  }
  const mapsUrl = (geo.lat && geo.lng) ? `https://www.google.com/maps?q=${geo.lat},${geo.lng}` : null;
  const flag = geo.country_code
    ? String.fromCodePoint(...geo.country_code.toUpperCase().split("").map(c => 0x1F1E6 + c.charCodeAt(0) - 65))
    : "";
  return (
    <div className="rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-3 space-y-1.5 text-sm">
      {hasGeo && (
        <div className="flex items-center gap-2 flex-wrap">
          {flag && <span className="text-lg leading-none">{flag}</span>}
          <span className="font-bold text-[#1A1A1A]">
            {[geo.city, geo.region, geo.country].filter(Boolean).join("، ") || "—"}
          </span>
        </div>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {hasIp && <InfoRow label="IP" value={data.ip} />}
        {geo.timezone && <InfoRow label="التوقيت" value={geo.timezone} />}
        {geo.isp && <InfoRow label="مزوّد الإنترنت" value={geo.isp} />}
        {(geo.lat && geo.lng) && (
          <div className="flex justify-between gap-2 py-1 sm:col-span-2">
            <dt className="text-[#6B6B6B]">الإحداثيات</dt>
            <dd className="text-[#1A1A1A] font-medium text-end">
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-[#E05D3A] underline">
                {Number(geo.lat).toFixed(4)}, {Number(geo.lng).toFixed(4)} · فتح Google Maps ↗
              </a>
            </dd>
          </div>
        )}
        {data?.at && <InfoRow label="التاريخ" value={new Date(data.at).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" })} />}
      </dl>
      {data?.user_agent && (
        <details className="pt-1 border-t border-[#EAEAEA]">
          <summary className="text-[10px] text-[#9B9B9B] cursor-pointer">User Agent</summary>
          <p className="text-[10px] text-[#6B6B6B] mt-1 break-all">{data.user_agent}</p>
        </details>
      )}
    </div>
  );
}
